import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Cable, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AddressComboBox, type ComboOption } from '@/components/forms/AddressComboBox';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { fetchFakeOdfPlan, attachFakeOdf } from '@/hooks/useAddressValidation';
import { searchPops } from '@/hooks/useAddressCreate';
import type { FakeOdfPlan, FiberCascadeOption } from '@uniticket/shared';

/**
 * Attach a fake ODF to an EXISTING address (user ask 2026-09-02). Admins only; the server gates the
 * route on SYSTEM_ADMIN and takes the address lock regardless of what this screen offers.
 *
 * Why it exists: the fake-ODF placeholder could only ever be born as a side effect — of a create, of an
 * add-box, or of the safety net that runs INSIDE a release. So an address that was already published and
 * simply had no fiber had no route at all: the verdict named « Fibre rattachée » as its one blocking
 * condition, and the only button that would have fixed it (« Publier ») is not rendered, precisely
 * because the release conditions are green. The console saw the problem and could do nothing.
 *
 * The screen proposes, the admin validates. Nothing is written by opening it: the plan is computed
 * server-side (PoP + provenance, the 0/0/0 slot, the derived deployment_type/quadrant) and the write
 * then sends back the PoP the screen showed — so it cannot land on a different one than the preview.
 *
 * PORTALLED TO document.body, and that is load-bearing: the console panel lives inside a GlassCard whose
 * `backdrop-filter` makes it the containing block of `position: fixed` descendants, so an overlay born
 * in that subtree covers the CARD and centres itself below the fold (incident 2026-08-11).
 */

const toCombo = (opts: FiberCascadeOption[]): ComboOption[] => opts.map((o) => ({ value: String(o.id), label: o.text }));

export function FakeOdfModal({
  jvid,
  addressLabel,
  onClose,
  onAttached,
}: {
  jvid: number;
  addressLabel?: string | null;
  onClose: () => void;
  onAttached: (jvid: number) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();

  const [plan, setPlan] = useState<FakeOdfPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [pop, setPop] = useState<ComboOption | null>(null);

  // Re-planned with an explicit PoP when the admin picks one: the preview must describe the PoP that
  // will actually be written, slot included — a chosen PoP can lack the 0/0/0 chain just as a derived
  // one can, and finding that out at write time would be finding it out too late.
  const load = useCallback(async (popId?: number) => {
    setLoading(true);
    setError(null);
    try {
      setPlan((await fetchFakeOdfPlan(jvid, popId)).data);
    } catch (err) {
      setError((err as Error)?.message || t('tickets:dmpLookup.fakeOdf.error'));
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [jvid, t]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const canWrite = plan != null && plan.popId != null && plan.portId != null && !plan.hasFiber && !saving && !loading;

  const submit = async () => {
    if (!plan?.popId || !canWrite) return;
    if (!confirmDmpWrite(t('tickets:dmpLookup.fakeOdf.confirmAction', { jvid }))) return;
    setSaving(true);
    setError(null);
    try {
      const res = await attachFakeOdf(jvid, plan.popId);
      toast.success(t('tickets:dmpLookup.fakeOdf.done', { utac: res.data.utac ?? '—' }));
      onAttached(jvid);
    } catch (err) {
      setError((err as Error)?.message || t('tickets:dmpLookup.fakeOdf.error'));
    } finally {
      setSaving(false);
    }
  };

  const row = (label: string, value: React.ReactNode, hint?: string | null) => (
    <div className="grid grid-cols-[130px_1fr] gap-2 border-t border-slate-100 px-3 py-2 first:border-t-0">
      <span className="text-slate-500">{label}</span>
      <span className="min-w-0">
        <span className="text-slate-800">{value}</span>
        {hint && <span className="block text-[10px] text-slate-400">{hint}</span>}
      </span>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('tickets:dmpLookup.fakeOdf.title')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <Cable className="h-4 w-4 text-brand-rose" />
            {t('tickets:dmpLookup.fakeOdf.title')}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {addressLabel ? `${addressLabel} · ` : ''}
            {t('tickets:dmpLookup.fakeOdf.subtitle', { jvid })}
          </p>
        </div>

        <div className="space-y-3 px-5 py-4">
          {loading && (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('tickets:dmpLookup.fakeOdf.loading')}
            </p>
          )}

          {error && (
            <p className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800" role="alert">
              {error}
            </p>
          )}

          {plan?.hasFiber && (
            <div className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2" role="note">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
              <span className="text-xs text-slate-700">{t('tickets:dmpLookup.fakeOdf.alreadyFibered')}</span>
            </div>
          )}

          {/* Le motif du serveur est repris TEL QUEL : « aucun frère fibré » et « ce PoP n'a pas de slot
              0/0/0 » appellent des gestes différents, et seul le serveur sait lequel s'est produit. */}
          {plan && !plan.hasFiber && plan.blocked && (
            <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2" role="note">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <span className="text-xs text-rose-800">
                {t('tickets:dmpLookup.fakeOdf.blocked', { reason: plan.blocked })}
              </span>
            </div>
          )}

          {plan && !plan.hasFiber && plan.rejected.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" role="note">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span className="text-xs text-amber-800">
                {t('tickets:dmpLookup.fakeOdf.rejected', {
                  list: plan.rejected.map((r) => `${r.popName ?? r.popId} (${r.reason})`).join(' · '),
                })}
              </span>
            </div>
          )}

          {/* L'aperçu : ce qui SERA écrit, PoP nommé et provenance dite. Un id de PoP seul ne se conteste
              pas — savoir qu'il vient du voisin d'à côté ou du bâtiment change ce qu'on en pense. */}
          {plan && !plan.hasFiber && plan.popId != null && (
            <div className="overflow-hidden rounded-xl border border-slate-200 text-xs">
              {row(
                t('tickets:dmpLookup.fakeOdf.colPop'),
                <>
                  <span className="font-medium">{plan.popName ?? `#${plan.popId}`}</span>
                  <span className="ml-1 text-slate-400">· #{plan.popId}</span>
                </>,
                plan.popSource ? t(`tickets:dmpLookup.fakeOdf.source.${plan.popSource}`) : null,
              )}
              {row(
                t('tickets:dmpLookup.fakeOdf.colSlot'),
                <span className="font-mono">0 / 0 / 0</span>,
                t('tickets:dmpLookup.fakeOdf.slotHint', { portId: plan.portId }),
              )}
              {row(
                t('tickets:dmpLookup.fakeOdf.colUtac'),
                <span className="text-slate-500">{t('tickets:dmpLookup.fakeOdf.utacPending')}</span>,
              )}
              {row(
                t('tickets:dmpLookup.fakeOdf.colDeployment'),
                <span className="font-mono">
                  {plan.deploymentType ?? '—'} / {plan.quadrant ?? '—'}
                </span>,
                t('tickets:dmpLookup.fakeOdf.deploymentHint', { code: plan.deliveryStatus ?? '—' }),
              )}
            </div>
          )}

          {/* Le choix manuel est le DERNIER recours de la cascade, pas son point de départ : il n'apparaît
              qu'à la demande, et re-calcule l'aperçu au lieu d'écrire directement. */}
          {plan && !plan.hasFiber && (
            picking ? (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {t('tickets:dmpLookup.fakeOdf.pickPop')}
                </label>
                <AddressComboBox
                  value={pop?.value ?? ''}
                  label={pop?.label ?? ''}
                  onChange={(o) => {
                    setPop(o);
                    if (o) void load(Number(o.value));
                  }}
                  fetcher={(q) => searchPops(q).then(toCombo)}
                  disabled={saving}
                  placeholder={t('tickets:addressMgmt.create.comboPlaceholder')}
                  emptyText={t('tickets:addressMgmt.create.comboNoResults')}
                  searchingText={t('tickets:addressMgmt.create.comboSearching')}
                  clearLabel={t('tickets:addressMgmt.create.clear')}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setPicking(true)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 underline underline-offset-2 hover:text-slate-700"
              >
                <Pencil className="h-3 w-3" />
                {t('tickets:dmpLookup.fakeOdf.pickPopToggle')}
              </button>
            )
          )}

          <p className="text-[11px] text-slate-400">{t('tickets:dmpLookup.fakeOdf.hint')}</p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving}>
            <X className="mr-1.5 h-4 w-4" />
            {t('tickets:dmpLookup.fakeOdf.cancel')}
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={!canWrite}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Cable className="mr-1.5 h-4 w-4" />}
            {t('tickets:dmpLookup.fakeOdf.confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
